import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  Key,
  AlertTriangle,
  CheckCircle2,
  Lock,
  Loader2,
  Mail,
  Clock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Layout } from "../components/Layout";
import { getConsulta } from "../lib/api";
import api from "../lib/api";
import { toast } from "sonner";

export default function ScrutinyPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [consulta, setConsulta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [scrutinyStatus, setScrutinyStatus] = useState(null);

  useEffect(() => {
    loadConsulta();
    loadScrutinyStatus();
  }, [id]);

  const loadConsulta = async () => {
    try {
      const response = await getConsulta(id);
      if (response.data.status !== "closed" && response.data.status !== "pending_blockchain") {
        toast.error(t('scrutiny.mustBeClosedError'));
        navigate(`/consultes/${id}`);
        return;
      }
      setConsulta(response.data);
    } catch (error) {
      toast.error(t('scrutiny.loadError'));
      navigate("/consultes");
    } finally {
      setLoading(false);
    }
  };

  const loadScrutinyStatus = async () => {
    try {
      const response = await api.get(`/scrutiny/status/${id}`);
      setScrutinyStatus(response.data);
    } catch (error) {
      // No pending request
      setScrutinyStatus({ status: 'none' });
    }
  };

  const handleRequestScrutiny = async () => {
    setRequesting(true);
    try {
      await api.post("/scrutiny/request", {
        consulta_id: id
      });
      
      toast.success(t('scrutiny.requestSent'));
      loadScrutinyStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('scrutiny.sendError'));
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/consultes/${id}`)}
            className="hover:bg-slate-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">
              {t('scrutiny.title')}
            </h1>
            <p className="text-slate-500">{consulta?.title}</p>
          </div>
        </div>

        {/* Status: Already has pending request */}
        {scrutinyStatus?.status === 'pending' && consulta?.status !== 'pending_blockchain' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 border border-amber-200 rounded-xl p-6"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-amber-900 mb-2">
                  {t('scrutiny.pendingRequest')}
                </h2>
                <p className="text-amber-700 mb-4">
                  {t('scrutiny.pendingRequestDesc')}
                </p>
                <p className="text-sm text-amber-600">
                  {t('scrutiny.sentAt')}: {scrutinyStatus.created_at ? (() => {
                    let s = String(scrutinyStatus.created_at).trim();
                    if (!s.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(s)) s += 'Z';
                    return new Date(s).toLocaleDateString('ca-ES', {
                      timeZone: 'Europe/Madrid',
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                  })() : t('common.unknown')}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Status: Pending blockchain writes */}
        {consulta?.status === 'pending_blockchain' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-orange-50 border border-orange-200 rounded-xl p-6"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Loader2 className="h-6 w-6 text-orange-600 animate-spin" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-orange-900 mb-2">
                  {t('status.pendingBlockchain')}
                </h2>
                <p className="text-orange-700">
                  {t('consultaDetail.pendingBlockchainDesc')}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Status: Admin approved, waiting for entity */}
        {scrutinyStatus?.status === 'admin_approved' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-50 border border-green-200 rounded-xl p-6"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-green-900 mb-2">
                  {t('scrutiny.approvedTitle')}
                </h2>
                <p className="text-green-700 mb-4">
                  {t('scrutiny.approvedDesc')}
                </p>
                <p className="text-sm text-green-600">
                  {t('scrutiny.approvedAt')}: {scrutinyStatus.admin_approved_at ? (() => {
                    let s = String(scrutinyStatus.admin_approved_at).trim();
                    if (!s.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(s)) s += 'Z';
                    return new Date(s).toLocaleDateString('ca-ES', {
                      timeZone: 'Europe/Madrid',
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                  })() : t('common.unknown')}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* No pending request - show request form */}
        {(scrutinyStatus?.status === 'none' || !scrutinyStatus) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-slate-200 rounded-xl p-6"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Lock className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-1">
                  {t('scrutiny.requestScrutiny')}
                </h2>
                <p className="text-slate-500">
                  {t('scrutiny.requestDesc')}
                </p>
              </div>
            </div>

            {/* Process explanation */}
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-slate-900 mb-3">{t('scrutiny.processTitle')}:</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-600">1</div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{t('scrutiny.step1Title')}</p>
                    <p className="text-xs text-slate-500">{t('scrutiny.step1Desc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center text-sm font-medium text-slate-600">2</div>
                  <div>
                    <p className="text-sm font-medium text-slate-700">{t('scrutiny.step2Title')}</p>
                    <p className="text-xs text-slate-500">{t('scrutiny.step2Desc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center text-sm font-medium text-slate-600">3</div>
                  <div>
                    <p className="text-sm font-medium text-slate-700">{t('scrutiny.step3Title')}</p>
                    <p className="text-xs text-slate-500">{t('scrutiny.step3Desc')}</p>
                  </div>
                </div>
              </div>
            </div>

            <Button
              onClick={handleRequestScrutiny}
              disabled={requesting}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="request-scrutiny-btn"
            >
              {requesting ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : (
                <Mail className="h-5 w-5 mr-2" />
              )}
              {t('scrutiny.requestScrutiny')}
            </Button>
          </motion.div>
        )}

        {/* Warning */}
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">{t('common.important')}</p>
              <p className="text-sm text-amber-700 mt-1">
                {t('scrutiny.irreversibleWarning')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
